import { useQuery } from '@tanstack/react-query';
import { qk } from '../queryKeys';
import { queryClient } from '../queryClient';
import { seedEntities, useEntity } from '../entities';
import * as api from '../backOfficeApi';

/**
 * Bust every organizer-directory variant (the list is keyed by search query, so
 * a new/edited organizer can't be patched into one known key). Call after
 * onboard/edit so the directory reflects it instead of a stale ≤60s copy.
 */
export function invalidateOrganizersList() {
  queryClient.invalidateQueries({ queryKey: qk.organizers.all });
}

const loadOrganizerDirectory = api.loadOrganizerDirectory as (opts?: {
  page?: number; search?: string; signal?: AbortSignal;
}) => Promise<any>;

/** Organizer directory (page 1, server-search) — seeds per-id organizer entities. */
export function useOrganizersList({ q = '' }: { q?: string } = {}) {
  return useQuery<any>({
    queryKey: qk.organizers.list({ q: q || '' }),
    queryFn: async ({ signal }) => {
      const res = await loadOrganizerDirectory({ page: 1, search: q || undefined, signal });
      seedEntities(qk.organizers.detail, res?.rows);
      return res;
    },
  });
}

/** One organizer by id — instant when a list already seeded it. */
export function useOrganizer(id: string | null | undefined) {
  return useEntity(qk.organizers.detail, id);
}

// Fetches + caches the full organization detail (members + events +
// invitations + stats) under qk.organizers.fullDetail(id) — ALSO used by
// entityById.ts's cold-link resolver, so both share one Query object and
// TanStack's own in-flight dedup collapses concurrent callers into one fetch
// (an `initialData` guess can't do this: two independent Query objects can
// each start fetching before either's result lands — this is why a shared key
// is required, not just a shared shape check).
export async function fetchOrganizerFullDetail(id: string): Promise<any> {
  const d = await (api.loadOrganizerDetails as any)(id);
  // Superset of the lightweight directory row also cached under
  // qk.organizers.detail(id) — safe to refresh it with the richer object.
  if (d) queryClient.setQueryData(qk.organizers.detail(id), d);
  return d;
}

// Full organizer detail — shared by the page header, the Profile tab and the
// name-resolution effect so they fetch ONCE instead of three times.
export function useOrganizerDetails(id: string | null | undefined) {
  return useQuery<any>({
    queryKey: qk.organizers.fullDetail(id || ''),
    queryFn: () => fetchOrganizerFullDetail(id || ''),
    enabled: !!id,
  });
}

/** Collected/net revenue for one organizer (order stats). */
export function useOrganizerCollected(id: string | null | undefined) {
  return useQuery<any>({
    queryKey: ['organizers', 'collected', id || ''],
    queryFn: ({ signal }) => (api.loadOrganizerCollected as any)(id, { signal }),
    enabled: !!id,
  });
}

/**
 * Verification/KYC status for one organizer — degrades gracefully (an org with
 * no verification record just resolves to no data; the header hides the chip).
 */
export function useOrganizerVerification(id: string | null | undefined) {
  return useQuery<any>({
    queryKey: ['organizers', 'verification', id || ''],
    queryFn: ({ signal }) => (api.loadKycOrganizationDetail as any)(id, { signal }),
    enabled: !!id,
    retry: false,
  });
}
